<template>
<div class="vp-root" :style="themePageStyle">
  <nav class="vp-topbar"><div class="container d-flex align-items-center py-2">
    <a href="javascript:void(0)" @click="$router.go(-1)" class="vp-back"><i class="mdi mdi-arrow-left"></i></a>
    <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:32px;" class="me-3" />
    <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:14px;">Vet Profile</span>
  </div></nav>

  <div class="container vp-container">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="!vet.user_id" class="text-center py-5">
      <i class="mdi mdi-account-off" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">Vet Not Found</h5>
    </div>
    <div v-else class="row g-4">
      <!-- Left: Profile -->
      <div class="col-lg-8">
        <!-- Header -->
        <div class="vp-card mb-3">
          <div class="d-flex align-items-start gap-3">
            <div class="vp-avatar"><img v-if="vet.avatar_url" :src="vet.avatar_url" /><span v-else style="font-size:28px;font-weight:700;color:#BFA164;">{{ (vet.display_name||'V').charAt(0) }}</span></div>
            <div class="flex-fill">
              <h4 class="fw-bold mb-1" style="color:var(--fm-h,#F5F0E8);">Dr. {{ vet.display_name }}</h4>
              <div v-if="vet.practice_name" style="color:var(--fm-m,#A09888);font-size:13px;">{{ vet.practice_name }}</div>
              <div v-if="vet.kvb_verified" class="mt-1" style="color:#50C878;font-size:12px;"><i class="mdi mdi-check-decagram me-1"></i>KVB Verified · {{ vet.kvb_registration_number }}</div>
              <div v-if="vet.years_of_experience" style="color:var(--fm-s,#6B6356);font-size:12px;margin-top:2px;">{{ vet.years_of_experience }} years experience</div>
              <div class="d-flex flex-wrap gap-1 mt-2">
                <span v-if="vet.is_mobile" class="vp-tag vp-tag-green"><i class="mdi mdi-car me-1"></i>Farm Visit</span>
                <span v-if="vet.offers_telemedicine" class="vp-tag vp-tag-blue"><i class="mdi mdi-video me-1"></i>Telemedicine</span>
                <span v-if="vet.offers_emergency" class="vp-tag vp-tag-red"><i class="mdi mdi-alarm-light me-1"></i>Emergency 24/7</span>
              </div>
            </div>
            <div v-if="vet.avg_rating" class="text-end">
              <div style="font-size:28px;font-weight:700;color:#FFD700;">{{ Number(vet.avg_rating).toFixed(1) }}</div>
              <div style="color:var(--fm-s,#6B6356);font-size:11px;">{{ vet.total_reviews }} reviews</div>
            </div>
          </div>
        </div>

        <!-- Bio -->
        <div v-if="vet.bio" class="vp-card mb-3">
          <h6 class="vp-section-title">About</h6>
          <p style="color:var(--fm-m,#A09888);font-size:13px;line-height:1.7;margin:0;">{{ vet.bio }}</p>
        </div>

        <!-- Location & Map -->
        <div v-if="vet.clinic_latitude && vet.clinic_longitude" class="vp-card mb-3">
          <h6 class="vp-section-title">Clinic Location</h6>
          <div v-if="vet.clinic_address" class="vp-loc-row">
            <i class="mdi mdi-map-marker me-1" style="color:#BFA164;"></i>
            <span style="color:var(--fm-h,#F5F0E8);font-size:13px;">{{ vet.clinic_address }}</span>
          </div>
          <div class="vp-loc-meta">
            <span><i class="mdi mdi-crosshairs-gps me-1"></i>{{ Number(vet.clinic_latitude).toFixed(5) }}, {{ Number(vet.clinic_longitude).toFixed(5) }}</span>
            <span v-if="vet.service_radius_km"><i class="mdi mdi-circle-outline me-1"></i>{{ vet.service_radius_km }}km service radius</span>
          </div>
          <div v-if="distanceFromMe !== null" class="vp-distance-pill">
            <i class="mdi mdi-near-me me-1"></i>
            <strong>{{ distanceFromMe.toFixed(1) }} km</strong> from your current location
            <span v-if="vet.service_radius_km && distanceFromMe > Number(vet.service_radius_km)" style="color:#FF9800;margin-left:8px;">(outside their service radius)</span>
          </div>
          <div class="vp-map-wrap">
            <iframe
              :src="osmEmbedUrl(vet.clinic_latitude, vet.clinic_longitude)"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              style="width:100%;height:240px;border:0;border-radius:10px;"></iframe>
          </div>
          <div class="d-flex gap-2 mt-2 flex-wrap">
            <a :href="googleMapsUrl(vet.clinic_latitude, vet.clinic_longitude)" target="_blank" class="btn btn-sm btn-outline-warning" style="font-size:11px;">
              <i class="mdi mdi-google-maps me-1"></i>Open in Google Maps
            </a>
            <a :href="googleDirectionsUrl(vet.clinic_latitude, vet.clinic_longitude)" target="_blank" class="btn btn-sm btn-outline-light" style="font-size:11px;">
              <i class="mdi mdi-directions me-1"></i>Get Directions
            </a>
          </div>
        </div>

        <!-- Specializations -->
        <div v-if="vet.specializations && vet.specializations.length" class="vp-card mb-3">
          <h6 class="vp-section-title">Specializations</h6>
          <div class="d-flex flex-wrap gap-2">
            <span v-for="s in vet.specializations" :key="s" class="vp-spec">{{ s.replace(/_/g,' ') }}</span>
          </div>
        </div>

        <!-- Fees -->
        <div class="vp-card mb-3">
          <h6 class="vp-section-title">Fees</h6>
          <div class="row g-2">
            <div class="col-6 col-md-3" v-if="vet.consultation_fee"><div class="vp-fee"><div class="vp-fee-val">KES {{ Number(vet.consultation_fee).toLocaleString() }}</div><div class="vp-fee-lbl">Consultation</div></div></div>
            <div class="col-6 col-md-3" v-if="vet.farm_visit_fee"><div class="vp-fee"><div class="vp-fee-val">KES {{ Number(vet.farm_visit_fee).toLocaleString() }}</div><div class="vp-fee-lbl">Farm Visit</div></div></div>
            <div class="col-6 col-md-3" v-if="vet.telemedicine_fee"><div class="vp-fee"><div class="vp-fee-val">KES {{ Number(vet.telemedicine_fee).toLocaleString() }}</div><div class="vp-fee-lbl">Telemedicine</div></div></div>
            <div class="col-6 col-md-3" v-if="vet.emergency_fee"><div class="vp-fee"><div class="vp-fee-val">KES {{ Number(vet.emergency_fee).toLocaleString() }}</div><div class="vp-fee-lbl">Emergency</div></div></div>
          </div>
        </div>

        <!-- Availability -->
        <div v-if="availability.length" class="vp-card mb-3">
          <h6 class="vp-section-title">Weekly Availability</h6>
          <div v-for="a in availability" :key="a.id" class="vp-avail-row">
            <span class="vp-avail-day">{{ dayName(a.day_of_week) }}</span>
            <span style="color:var(--fm-h,#F5F0E8);font-size:12px;">{{ a.start_time.substring(0,5) }} — {{ a.end_time.substring(0,5) }}</span>
            <span style="color:var(--fm-s,#6B6356);font-size:10px;">{{ a.slot_duration_minutes }}min slots</span>
          </div>
        </div>

        <!-- Reviews -->
        <div class="vp-card">
          <h6 class="vp-section-title">Reviews ({{ reviews.length }})</h6>
          <div v-if="!reviews.length" style="color:var(--fm-s,#6B6356);font-size:12px;padding:10px 0;">No reviews yet</div>
          <div v-for="r in reviews" :key="r.rated_at" class="vp-review">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <span style="color:var(--fm-h,#F5F0E8);font-weight:600;font-size:13px;">{{ r.farmer_name }}</span>
              <span style="color:#FFD700;font-size:12px;"><i v-for="s in 5" :key="s" class="mdi" :class="s<=r.farmer_rating?'mdi-star':'mdi-star-outline'"></i></span>
            </div>
            <div style="color:var(--fm-m,#A09888);font-size:12px;">{{ r.farmer_review || 'No written review' }}</div>
            <div style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:4px;">{{ r.consultation_type.replace(/_/g,' ') }} · {{ formatDate(r.rated_at) }}</div>
          </div>
        </div>
      </div>

      <!-- Right: Booking -->
      <div class="col-lg-4">
        <div class="vp-card vp-booking-card" style="position:sticky;top:80px;">
          <h6 class="vp-section-title">Book Appointment</h6>
          <div class="mb-2"><label class="vp-label">Consultation Type *</label>
            <select v-model="bookForm.consultationType" class="form-select vp-input">
              <option value="in_person" v-if="vet.is_mobile">Farm Visit</option>
              <option value="telemedicine_video" v-if="vet.offers_telemedicine">Video Call</option>
              <option value="telemedicine_chat" v-if="vet.offers_telemedicine">Chat Consultation</option>
              <option value="emergency" v-if="vet.offers_emergency">Emergency</option>
            </select>
          </div>
          <div class="mb-2"><label class="vp-label">Reason *</label><input v-model="bookForm.reason" class="form-control vp-input" placeholder="e.g. Cow not eating, suspected FMD..." /></div>
          <div class="mb-2"><label class="vp-label">Describe Symptoms</label><textarea v-model="bookForm.symptomsDescription" class="form-control vp-input" rows="3" placeholder="Provide details about the animal's condition..."></textarea></div>
          <div class="row g-2 mb-2">
            <div class="col-6"><label class="vp-label">Date</label><input v-model="bookForm.scheduledDate" class="form-control vp-input" type="date" /></div>
            <div class="col-6"><label class="vp-label">Time</label><input v-model="bookForm.scheduledTime" class="form-control vp-input" type="time" /></div>
          </div>
          <div class="mb-3">
            <label class="vp-label">Visit Address</label>
            <div class="d-flex gap-1">
              <input v-model="bookForm.visitAddress" class="form-control vp-input flex-fill" placeholder="Farm location or address" @input="onAddressInput" />
              <button class="btn btn-sm btn-outline-warning" type="button" @click="useMyLocation" :disabled="geocodeBusy" title="Use my current location">
                <i class="mdi mdi-crosshairs-gps"></i>
              </button>
            </div>
            <div v-if="geocodeBusy" class="vp-addr-hint"><span class="spinner-border spinner-border-sm me-1" style="width:10px;height:10px;"></span>Locating...</div>
            <div v-else-if="addressDistance !== null" class="vp-addr-hint vp-addr-ok">
              <i class="mdi mdi-map-marker-distance me-1"></i>
              <strong>{{ addressDistance.toFixed(1) }} km</strong> from {{ vet.practice_name || 'the clinic' }}
              <span v-if="vet.service_radius_km && addressDistance > Number(vet.service_radius_km)" style="color:#FF9800;">— outside service radius</span>
            </div>
            <div v-else-if="addressGeocodeError" class="vp-addr-hint" style="color:#FF9800;">
              <i class="mdi mdi-alert-outline me-1"></i>{{ addressGeocodeError }}
            </div>
          </div>

          <div v-if="estimatedFee" class="mb-3 p-2" style="background:var(--fm-bg3,#1E1E1E);border-radius:8px;text-align:center;">
            <div style="color:var(--fm-s,#6B6356);font-size:10px;">Estimated Fee</div>
            <div style="color:#BFA164;font-size:18px;font-weight:700;">KES {{ Number(estimatedFee).toLocaleString() }}</div>
          </div>

          <button class="btn btn-warning w-100 fw-bold" @click="bookAppointment" :disabled="!bookForm.reason||booking">
            <span v-if="booking"><span class="spinner-border spinner-border-sm me-1"></span></span>
            <i v-else class="mdi mdi-calendar-check me-1"></i>Book Now
          </button>
          <div v-if="bookError" class="mt-2" style="color:#FF6B6B;font-size:12px;text-align:center;">{{ bookError }}</div>
          <div v-if="bookSuccess" class="mt-2" style="color:#50C878;font-size:12px;text-align:center;">{{ bookSuccess }}</div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] .vp-root{background:#F7F5F2!important}
html[data-theme="light"] .vp-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .vp-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .vp-input{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .vp-fee{background:#F7F5F2!important;border-color:#E8E2DA!important}
html[data-theme="light"] .vp-review{border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .vp-avail-row{border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .vp-spec{background:#F0EBE4!important;color:#555!important}
</style>

<style scoped>
.vp-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh}
.vp-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.vp-back{color:var(--fm-m,#A09888);font-size:22px;margin-right:12px;text-decoration:none}
.vp-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.vp-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:20px}
.vp-section-title{color:#BFA164;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:14px}
.vp-avatar{width:72px;height:72px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0}
.vp-avatar img{width:100%;height:100%;object-fit:cover}
.vp-tag{font-size:10px;padding:3px 10px;border-radius:8px;font-weight:600}
.vp-tag-green{background:rgba(80,200,120,.1);color:#50C878}
.vp-tag-blue{background:rgba(79,195,247,.1);color:#4FC3F7}
.vp-tag-red{background:rgba(255,107,107,.1);color:#FF6B6B}
.vp-spec{background:var(--fm-bg3,#1E1E1E);color:var(--fm-m,#A09888);padding:4px 12px;border-radius:16px;font-size:12px;text-transform:capitalize}
.vp-fee{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#1E1E1E);border-radius:8px;padding:10px;text-align:center}
.vp-fee-val{color:#BFA164;font-weight:700;font-size:14px}
.vp-fee-lbl{color:var(--fm-s,#6B6356);font-size:10px;margin-top:2px}
.vp-avail-row{display:flex;align-items:center;gap:12px;padding:8px 0;border-bottom:1px solid var(--fm-br,#1A1A1A)}
.vp-avail-row:last-child{border-bottom:none}
.vp-avail-day{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:12px;min-width:80px}
.vp-review{padding:12px 0;border-bottom:1px solid var(--fm-br,#1A1A1A)}
.vp-review:last-child{border-bottom:none}
.vp-label{display:block;color:var(--fm-s,#6B6356);font-size:11px;font-weight:500;margin-bottom:4px}
.vp-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.vp-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.vp-booking-card{border-color:rgba(191,161,100,.3)}
.vp-loc-row{display:flex;align-items:flex-start;gap:6px;margin-bottom:8px;line-height:1.5}
.vp-loc-meta{display:flex;flex-wrap:wrap;gap:14px;font-size:11px;color:var(--fm-s,#6B6356);margin-bottom:10px}
.vp-loc-meta span{display:inline-flex;align-items:center;gap:2px}
.vp-distance-pill{background:rgba(191,161,100,.08);border:1px solid rgba(191,161,100,.4);color:#BFA164;padding:6px 10px;border-radius:8px;font-size:12px;margin-bottom:10px;display:inline-block}
.vp-map-wrap{border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;overflow:hidden;background:var(--fm-bg2,#0A0A0A)}
html[data-theme="light"] .vp-map-wrap{border-color:#E0D8D0}
.vp-addr-hint{font-size:11px;color:var(--fm-s,#6B6356);margin-top:4px;display:flex;align-items:center}
.vp-addr-ok{color:#50C878}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "VetProfile",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,loading:true,
    vet:{},availability:[],reviews:[],stats:{},
    bookForm:{consultationType:'in_person',reason:'',symptomsDescription:'',scheduledDate:'',scheduledTime:'',visitAddress:''},
    booking:false,bookError:'',bookSuccess:'',
    myLat:null,myLng:null,
    addressLat:null,addressLng:null,addressDistance:null,addressGeocodeError:'',
    geocodeBusy:false,_geocodeTimer:null
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    estimatedFee(){
      if(!this.vet.user_id)return 0;
      var t=this.bookForm.consultationType;
      if(t==='in_person')return this.vet.farm_visit_fee||this.vet.consultation_fee||0;
      if(t.indexOf('telemedicine')!==-1)return this.vet.telemedicine_fee||0;
      if(t==='emergency')return this.vet.emergency_fee||0;
      return this.vet.consultation_fee||0;
    },
    distanceFromMe(){
      if (this.myLat == null || this.myLng == null) return null;
      if (!this.vet.clinic_latitude || !this.vet.clinic_longitude) return null;
      return this.haversineKm(this.myLat, this.myLng, Number(this.vet.clinic_latitude), Number(this.vet.clinic_longitude));
    }
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';
    this.syncTheme();this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.bookForm.scheduledDate=new Date().toISOString().split('T')[0];
    this.loadVet();
    this.captureMyLocation();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect()},
  methods:{
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    dayName(d){return['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][d]||''},
    formatDate(d){if(!d)return'';return new Date(d).toLocaleDateString('en-KE',{day:'numeric',month:'short',year:'numeric'})},
    haversineKm(lat1, lon1, lat2, lon2){
      var R = 6371, toRad = function(x){return x * Math.PI / 180};
      var dLat = toRad(lat2-lat1), dLon = toRad(lon2-lon1);
      var a = Math.sin(dLat/2)*Math.sin(dLat/2) + Math.cos(toRad(lat1))*Math.cos(toRad(lat2)) * Math.sin(dLon/2)*Math.sin(dLon/2);
      return 2 * R * Math.asin(Math.sqrt(a));
    },
    osmEmbedUrl(lat, lng){
      lat = Number(lat); lng = Number(lng);
      var d = 0.01; // ~1.1km bounding box
      var bbox = (lng - d) + ',' + (lat - d) + ',' + (lng + d) + ',' + (lat + d);
      return 'https://www.openstreetmap.org/export/embed.html?bbox=' + bbox + '&layer=mapnik&marker=' + lat + ',' + lng;
    },
    googleMapsUrl(lat, lng){
      return 'https://www.google.com/maps?q=' + lat + ',' + lng;
    },
    googleDirectionsUrl(lat, lng){
      return 'https://www.google.com/maps/dir/?api=1&destination=' + lat + ',' + lng;
    },
    captureMyLocation(){
      if (!navigator.geolocation) return;
      var self = this;
      navigator.geolocation.getCurrentPosition(
        function(p){ self.myLat = p.coords.latitude; self.myLng = p.coords.longitude; },
        function(){ /* ignore — permission denied or timeout */ },
        { timeout: 5000, maximumAge: 60000 }
      );
    },
    useMyLocation(){
      if (!navigator.geolocation) { this.addressGeocodeError = 'Geolocation not available'; return; }
      var self = this; this.geocodeBusy = true; this.addressGeocodeError = '';
      navigator.geolocation.getCurrentPosition(
        async function(p){
          self.myLat = p.coords.latitude; self.myLng = p.coords.longitude;
          self.addressLat = p.coords.latitude; self.addressLng = p.coords.longitude;
          self.computeAddressDistance();
          // Reverse geocode to populate the visit address field
          try {
            var r = await fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat=' + p.coords.latitude + '&lon=' + p.coords.longitude);
            var j = await r.json();
            if (j && j.display_name) self.bookForm.visitAddress = j.display_name;
          } catch(e){}
          self.geocodeBusy = false;
        },
        function(){ self.geocodeBusy = false; self.addressGeocodeError = 'Could not get your location'; },
        { timeout: 8000 }
      );
    },
    onAddressInput(){
      this.addressDistance = null;
      this.addressGeocodeError = '';
      var self = this;
      if (this._geocodeTimer) clearTimeout(this._geocodeTimer);
      var q = (this.bookForm.visitAddress || '').trim();
      if (q.length < 4) return;
      this._geocodeTimer = setTimeout(function(){ self.geocodeAddress(q); }, 700);
    },
    async geocodeAddress(q){
      this.geocodeBusy = true; this.addressGeocodeError = '';
      try {
        var url = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=ke&q=' + encodeURIComponent(q);
        var r = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        var j = await r.json();
        if (j && j.length) {
          this.addressLat = Number(j[0].lat);
          this.addressLng = Number(j[0].lon);
          this.computeAddressDistance();
        } else {
          this.addressGeocodeError = "Couldn't locate that address — try adding city or county.";
        }
      } catch(e){
        this.addressGeocodeError = 'Address lookup failed.';
      }
      this.geocodeBusy = false;
    },
    computeAddressDistance(){
      if (this.addressLat == null || !this.vet.clinic_latitude) { this.addressDistance = null; return; }
      this.addressDistance = this.haversineKm(this.addressLat, this.addressLng, Number(this.vet.clinic_latitude), Number(this.vet.clinic_longitude));
    },
    async loadVet(){
      this.loading=true;
      var vetUserId=this.$route.params.vetUserId||this.$route.params.id;
      try{
        var r=await executeJson({function:'getVetProfile',vetUserId:vetUserId},flocker);
        if(r.data&&r.data.success&&r.data.data){
          this.vet=r.data.data.vet||{};
          this.availability=r.data.data.availability||[];
          this.reviews=r.data.data.reviews||[];
          this.stats=r.data.data.stats||{};
          if(this.vet.offers_telemedicine&&!this.vet.is_mobile)this.bookForm.consultationType='telemedicine_video';
          if (this.addressLat != null) this.computeAddressDistance();
        }
      }catch(e){console.error(e)}
      this.loading=false;
    },
    async bookAppointment(){
      this.booking=true;this.bookError='';this.bookSuccess='';
      var userId=sessionStorage.getItem('userId');
      if(!userId){this.$router.push('/login');return}
      try{
        var params={function:'bookConsultation',vetUserId:this.vet.user_id,farmerUserId:userId,
          consultationType:this.bookForm.consultationType,reason:this.bookForm.reason,
          symptomsDescription:this.bookForm.symptomsDescription,scheduledDate:this.bookForm.scheduledDate,
          scheduledTime:this.bookForm.scheduledTime,visitAddress:this.bookForm.visitAddress};
        var r=await executeJson(params,flocker);
        if(r.data&&r.data.success){
          this.bookSuccess='Appointment booked! Ref: '+(r.data.data.consultationRef||'');
          this.bookForm={consultationType:this.bookForm.consultationType,reason:'',symptomsDescription:'',scheduledDate:new Date().toISOString().split('T')[0],scheduledTime:'',visitAddress:''};
        }else{this.bookError=r.data.message||'Booking failed'}
      }catch(e){this.bookError='Network error'}
      this.booking=false;
    }
  }
};
</script>
